import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { decode } from 'he';
import { getActionLabel, getActionSentTimeMs } from '../custom-components/TableRowDetailContent/actionUtils';
import { getAlertCommentsForDisplay } from '../custom-components/TableRowDetailSharedUtils';

/** One-line text for PDF list cells; pass full alert row (uses issues + nonCoveredCommentList). */
const formatIssuesForListCell = (alertRow) => {
    const rows = getAlertCommentsForDisplay(alertRow);
    if (!rows.length) return '';
    try {
        return rows
            .map((issue) => {
                const reason = issue?.reason != null && issue?.reason !== '' ? String(issue.reason) : '';
                const body = issue?.comment != null && issue?.comment !== '' ? String(issue.comment) : '';
                if (reason && body) return `${reason}: ${body}`;
                return reason || body;
            })
            .filter(Boolean)
            .join(' | ');
    } catch {
        return '';
    }
};

export const formatDateOnly = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString();
};
// Date Fns is used to format the dates we receive
// from our API call
// import { format } from "date-fns";

// define a generatePDF function that accepts a tickets argument
export const generatePDF = (data) => {
    // initialize jsPDF
    const doc = new jsPDF();

    // define the columns we want and their titles
    const tableColumn = ['RX Number', 'Drug', 'Dispense Date', 'Days Supply', 'Qty', 'Total Price'];
    // define an empty array of rows
    const tableRows = [];

    // for each ticket pass all its data into an array
    data.admissionMedsDetailsList.forEach((row) => {
        const ticketData = [
            row.rxNo,
            row.drug,
            new Date(row.dispenseDt).toLocaleDateString(),
            row.daysSupply,
            row.qty,
            '$' + row.ttlPrice,
            // called date-fns to format the date on the ticket
        ];
        // push each tickcet's info into a row
        tableRows.push(ticketData);
    });

    const pageHeight = doc.internal.pageSize.height || doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();

    // startY is basically margin-top
    doc.setFont('Helvetica');
    doc.autoTable(tableColumn, tableRows, { startY: 78, headStyles: { fillColor: '#452f69' } });
    const date = Date().split(' ');
    // we use a date string to generate our filename.
    const dateStr = date[0] + date[1] + date[2] + date[3] + date[4];
    // ticket title. and margin-top + margin-left
    doc.setFontSize(25);
    doc.text('Admission / Medication Review', pageWidth / 2, 20, { align: 'center' });
    doc.setLineWidth(0.5);
    doc.line(14, 25, pageWidth - 14, 25);
    doc.setFontSize(15);
    // center text
    // doc.text('Admission Date: ' + new Date(data.admDate).toLocaleDateString(), pageWidth / 2, 35, {align: 'center'});
    // doc.text('Patient Name: ' + data.patName, pageWidth / 2, 45,{align: 'center'});
    // doc.text('Facility Name: ' + data.facName, pageWidth / 2, 55,{align: 'center'});
    // start from left
    doc.text('Admission Date: ' + new Date(data.admDate).toLocaleDateString(), 14, 35);
    doc.text('Patient Name: ' + data.patName, 14, 45);
    doc.text('Facility Name: ' + data.facName, 14, 55);
    doc.text('Nursing Station: ' + data.nsID + '  Room: ' + data.room + '  Bed: ' + data.bed, 14, 65);
    doc.setLineWidth(0.2);
    doc.line(14, 68, pageWidth - 14, 68);
    doc.setFontSize(13);
    doc.text(
        'Total Meds: ' + data.admissionMedsDetailsList.length + '  Grand Total: $' + data.sumTtlPrice,
        pageWidth - 14,
        75,
        { align: 'right' }
    );
    // we define the name of our PDF file.
    // doc.autoPrint();
    // window.open(doc.output('bloburl'), '_blank');
    return doc;
};

export const printPumpPickupList = (data) => {
    const doc = new jsPDF();

    const tableColumn = [
        'Patient',
        'Facility',
        'Nursing Station',
        'Serial Number',
        'Requested Date',
        'Notes / comments',
    ];
    const tableRows = [];

    data.forEach((row) => {
        const notes =
            (row.rxLabelText != null && String(row.rxLabelText).trim() !== '' && String(row.rxLabelText)) ||
            formatIssuesForListCell(row) ||
            '';
        const requestedDate = row.lastPickupRequest
            ? formatDateOnly(row.lastPickupRequest)
            : row.returnDueDt
              ? `Due: ${formatDateOnly(row.returnDueDt)}`
              : 'return';
        const ticketData = [row.patName, row.facName, row.nsID, row.pumpSerialNo, requestedDate, notes];
        // push each tickcet's info into a row
        tableRows.push(ticketData);
    });

    const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();

    // startY is basically margin-top
    doc.setFontSize(25);
    doc.text('Pump Pickup Requests', pageWidth / 2, 20, { align: 'center' });
    doc.setLineWidth(0.5);
    doc.line(14, 25, pageWidth - 14, 25);
    doc.setFont('Helvetica');
    doc.autoTable(tableColumn, tableRows, {
        startY: 35,
        headStyles: { fillColor: '#452f69' },
        styles: { fontSize: 9, cellPadding: 2, overflow: 'linebreak' },
        columnStyles: {
            4: { cellWidth: 31, overflow: 'visible' },
            5: { cellWidth: 42 },
        },
    });
    return doc;
};

export const printNarcoticsRefillList = (data, title = 'Narcotics - New Scripts Needed') => {
    // Landscape + more columns so the whole alert detail (drug, directions, qty, day supply,
    // qty remaining, etc.) prints in one list instead of requiring a per-alert print.
    const doc = new jsPDF({ orientation: 'landscape' });
    const valueOrDash = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        return String(value);
    };

    const tableColumn = [
        'Patient',
        'Rx #',
        'Facility',
        'Station',
        'Drug Name',
        'Directions',
        'Qty',
        'Days Supply',
        'Qty Remaining',
        'Prescriber',
    ];

    const tableRows = data.map((row) => [
        valueOrDash(row.patName),
        valueOrDash(row.rxNo),
        valueOrDash(row.facName),
        valueOrDash(row.nsID),
        valueOrDash(row.drugLabelName),
        valueOrDash(row.sig),
        valueOrDash(row.qty),
        valueOrDash(row.daysSupply),
        valueOrDash(row.qtyRemaining),
        valueOrDash(row.phName),
    ]);

    const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();

    // startY is basically margin-top
    doc.setFontSize(18);
    doc.text(title, pageWidth / 2, 15, { align: 'center' });
    doc.setLineWidth(0.5);
    doc.line(14, 20, pageWidth - 14, 20);
    doc.setFont('Helvetica');
    doc.autoTable(tableColumn, tableRows, {
        startY: 26,
        headStyles: { fillColor: '#452f69' },
        styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
        columnStyles: { 5: { cellWidth: 45 } },
    });
    return doc;
};

export const printNarcoticsRefillTicket = (data) => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
    const valueOrDash = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        return String(value);
    };
    const getFirstValue = (...values) => values.find((value) => value !== null && value !== undefined && value !== '');
    const formatDateIfPresent = (value) => {
        if (!value) return '-';
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? valueOrDash(value) : parsed.toLocaleDateString();
    };

    const patientName = getFirstValue(data.patName, data.patientName);
    const rxNumber = getFirstValue(data.rxNo, data.rxNumber);
    const drugName = getFirstValue(data.drugLabelName, data.drugName);
    const directions = getFirstValue(data.sig, data.directions, data.instructions);
    const facilityName = getFirstValue(data.facName, data.facilityName);
    const nursingStation = getFirstValue(data.nsID, data.nursingStationId);
    const prescriberName = getFirstValue(data.phName, data.prescriberName, data.prescriber);
    const quantity = getFirstValue(data.qty, data.quantity);
    const daysSupply = getFirstValue(data.daysSupply, data.daySupply);
    const qtyRemaining = getFirstValue(data.qtyRemaining, data.minDrugQty);
    const totalQtyPrescribed = getFirstValue(data.totalQtyWritten, data.totalQtyPrescribed);
    const lastFillDate = formatDateIfPresent(getFirstValue( data.lastFillDt ));

    doc.setFontSize(22);
    doc.text('Narcotics - New Script Needed', pageWidth / 2, 20, { align: 'center' });
    doc.setLineWidth(0.5);
    doc.line(14, 25, pageWidth - 14, 25);
    doc.setFont('Helvetica');
    doc.setFontSize(15);
    doc.text('Patient Name: ' + valueOrDash(patientName), 14, 35, { align: 'left' });
    doc.text('Rx Number: ' + valueOrDash(rxNumber), 14, 43, { align: 'left' });
    doc.text('Drug Name: ' + valueOrDash(drugName), 14, 51, { align: 'left' });
    doc.text('Directions: ' + valueOrDash(directions), 14, 59, { align: 'left' });
    doc.line(14, 63, pageWidth - 14, 63);
    doc.text('Facility Name: ' + valueOrDash(facilityName), 14, 73, { align: 'left' });
    doc.text('Nursing Station: ' + valueOrDash(nursingStation), 14, 81, { align: 'left' });
    doc.text('Prescriber Name: ' + valueOrDash(prescriberName), 14, 89, { align: 'left' });
    doc.line(14, 92, pageWidth - 14, 92);
    doc.text('Quantity: ' + valueOrDash(quantity), 14, 102, { align: 'left' });
    doc.text('Days Supply: ' + valueOrDash(daysSupply), 14, 110, { align: 'left' });
    doc.text('Last Fill Date: ' + valueOrDash(lastFillDate), 14, 118, { align: 'left' });
    doc.text('Qty Remaining: ' + valueOrDash(qtyRemaining), 14, 126, { align: 'left' });
    doc.text('Total Qty Prescribed: ' + valueOrDash(totalQtyPrescribed), 14, 134, { align: 'left' });
    doc.line(14, 137, pageWidth - 14, 137);
    const issueText = formatIssuesForListCell(data);
    if (issueText) {
        let y = 145;
        doc.setFontSize(12);
        doc.text('Comments:', 14, y);
        y += 9;
        doc.setFontSize(10);
        let decoded = issueText;
        try {
            decoded = decode(issueText);
        } catch {
            decoded = issueText;
        }
        const lines = doc.splitTextToSize(decoded, pageWidth - 28);
        lines.forEach((line) => {
            if (y > doc.internal.pageSize.getHeight() - 20) {
                doc.addPage();
                y = 20;
            }
            doc.text(line, 14, y);
            y += 7;
        });
    }
    return doc;
};

export const printAlertDetailTicket = (data, title = 'Alert Detail', category) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
    const hasValue = (value) => value !== null && value !== undefined && value !== '';
    const valueOrDash = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        return String(value);
    };
    const getFirstValue = (...values) => values.find((value) => hasValue(value));
    const formatDateIfPresent = (value) => {
        if (!value) return '-';
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? valueOrDash(value) : parsed.toLocaleDateString();
    };
    const formatDate = (value, includeTime = false) => {
        if (!hasValue(value)) return '';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return String(value);
        return includeTime ? parsed.toLocaleString() : parsed.toLocaleDateString();
    };
    const formatMoney = (value) => {
        if (!hasValue(value)) return '';
        const numberValue = Number(value);
        if (Number.isNaN(numberValue)) return `$${value}`;
        return `$${numberValue.toFixed(2)}`;
    };

    const meds = data?.admissionMedsDetailsList || [];

    const admissionDate = formatDate(getFirstValue(data?.admDate, data?.admissionDate));
    const patientName = getFirstValue(data?.patName, data?.patientName);
    const facilityName = getFirstValue(data?.facName, data?.facilityName);
    const nursingStation = getFirstValue(data?.nsID, data?.nursingStationId);
    const room = getFirstValue(data?.room, data?.roomNumber);
    const bed = getFirstValue(data?.bed, data?.bedNumber);

    const rxNumber = getFirstValue(data?.rxNo, data?.rxNumber);
    const ndc = getFirstValue(data?.ndc, data?.NDC);
    const dispenseDate = formatDate(getFirstValue(data?.dispenseDt, data?.dispensedOn));
    const alertedOn = formatDate(getFirstValue(data?.alertCreatedOn, data?.dateCreated, data?.receivedOn), true);
    const lastFillDate = formatDate(getFirstValue(data?.lastFillDt));
    const drugName = getFirstValue(data?.drugLabelName, data?.drugName);
    const isNarcoticsRefill = category === 'narcoticsRefill';
    const qtyLabel = isNarcoticsRefill ? 'Dispense Qty' : 'Qty';
    const qty = isNarcoticsRefill
        ? getFirstValue(data?.totalQtyDispensed, data?.qty, data?.quantity)
        : getFirstValue(data?.qty, data?.quantity);
    const daysSupply = getFirstValue(data?.daysSupply, data?.daySupply);
    const totalPrice = formatMoney(data?.ttlPrice);
    const prescriber = getFirstValue(data?.phName, data?.prescriberName);
    const prescribingDoctor = getFirstValue(data.prescribingDoctor);
    const patientDoctor = getFirstValue(data.patDoctor);
    const directions = getFirstValue(data?.sig, data?.directions);
    const expireDate = formatDate(getFirstValue(data.expireDt, data.expiration));

    doc.setFontSize(20);
    doc.text(title || 'Alert Detail', pageWidth / 2, 20, { align: 'center' });
    doc.setLineWidth(0.5);
    doc.line(14, 25, pageWidth - 14, 25);
    doc.setFont('Helvetica');
    doc.setFontSize(13);

    let y = 35;
    const lineHeight = 9;

    const addLine = (label, value) => {
        if (!hasValue(value)) return;

        const text = `${label}: ${value}`;
        const lines = doc.splitTextToSize(text, pageWidth - 28);

        lines.forEach((line) => {
            doc.text(line, 14, y);
            y += lineHeight;
        });
    };

    addLine('Admission Date', admissionDate);
    addLine('Patient Name', patientName);
    addLine('Facility Name', facilityName);

    const nursingStationParts = [];
    if (hasValue(nursingStation)) nursingStationParts.push(`Nursing Station: ${nursingStation}`);
    if (hasValue(room)) nursingStationParts.push(`Room: ${room}`);
    if (hasValue(bed)) nursingStationParts.push(`Bed: ${bed}`);
    if (nursingStationParts.length) {
        doc.text(nursingStationParts.join('  '), 14, y);
        y += lineHeight;
    }

    addLine('RX Number', rxNumber);
    addLine('NDC', ndc);
    // addLine('Prescriber Name', prescriber);
    addLine('Physician  Name', patientDoctor);
    addLine('Dispense Date', dispenseDate);
    addLine('Alerted On', alertedOn);
    addLine('Last Fill Date', lastFillDate);
    addLine('Drug Label Name', drugName);
    addLine(qtyLabel, qty);
    addLine('Days Supply', daysSupply);
    addLine('Total Price', totalPrice);
    addLine('Directions', directions);
    addLine('RX Expiration Date', expireDate);
    addLine('Rx Prescriber', prescribingDoctor);

    y += 1;
    doc.line(14, y, pageWidth - 14, y);
    y += 6;

    if (Array.isArray(meds) && meds.length > 0) {
        const medsRows = meds.map((row) => [
            getFirstValue(row?.rxNo, row?.rxNumber, ''),
            getFirstValue(row?.drug, row?.drugLabelName, ''),
            formatDate(getFirstValue(row?.dispenseDt, row?.dispensedOn)),
            getFirstValue(row?.daysSupply, row?.daySupply, ''),
            getFirstValue(row?.qty, row?.quantity, ''),
            formatMoney(row?.ttlPrice),
        ]);

        const medsTotal = getFirstValue(data?.sumTtlPrice, data?.grandTotal);
        const totalMedsText = `Total Meds: ${meds.length}`;
        const grandTotalText = hasValue(medsTotal) ? `  Grand Total: ${formatMoney(medsTotal)}` : '';
        doc.text(`${totalMedsText}${grandTotalText}`, pageWidth - 14, y, { align: 'right' });
        y += 4;

        doc.autoTable(['RX Number', 'Drug', 'Dispense Date', 'Days Supply', 'Qty', 'Total Price'], medsRows, {
            startY: y,
            headStyles: { fillColor: '#452f69' },
            styles: { fontSize: 11, cellPadding: 2 },
        });
        y = doc.lastAutoTable.finalY + 10;
    }

    const pageHeight = doc.internal.pageSize.height || doc.internal.pageSize.getHeight();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2;
    const sectionGap = 6;
    const bodyLineHeight = 7;
    const safeDecode = (value) => {
        if (!hasValue(value)) return '';
        try {
            return decode(String(value));
        } catch {
            return String(value);
        }
    };
    const ensureSpace = (minLines = 1) => {
        const need = minLines * bodyLineHeight + sectionGap;
        if (y + need > pageHeight - 12) {
            doc.addPage();
            y = margin;
        }
    };
    const writeBoldLine = (label) => {
        ensureSpace(1);
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(label, margin, y);
        y += bodyLineHeight + 2;
        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(11);
    };
    const writeWrapped = (text) => {
        const body = safeDecode(text);
        if (!body) return;
        const lines = doc.splitTextToSize(body, contentWidth);
        lines.forEach((line) => {
            ensureSpace(1);
            doc.text(line, margin, y);
            y += bodyLineHeight;
        });
    };

    const billingComment = getFirstValue(data?.billingComment);
    if (hasValue(billingComment)) {
        writeBoldLine('Billing comment');
        writeWrapped(billingComment);
        y += sectionGap;
    }

    const rxNotes = getFirstValue(data?.rxLabelText);
    if (hasValue(rxNotes)) {
        writeBoldLine('Notes');
        writeWrapped(rxNotes);
        y += sectionGap;
    }

    const prescribedDrug = getFirstValue(data?.prescribedDrugName);
    if (hasValue(prescribedDrug)) {
        ensureSpace(1);
        doc.text(`Prescribed drug: ${safeDecode(prescribedDrug)}`, margin, y);
        y += bodyLineHeight + sectionGap;
    }

    const issues = getAlertCommentsForDisplay(data);
    if (issues.length > 0) {
        writeBoldLine(`Comments (${issues.length})`);
        issues.forEach((issue, idx) => {
            ensureSpace(4);
            if (idx > 0) {
                doc.setLineWidth(0.2);
                doc.line(margin, y, pageWidth - margin, y);
                y += sectionGap;
            }
            const reason = getFirstValue(issue?.reason);
            if (hasValue(reason)) {
                doc.setFont('Helvetica', 'bold');
                doc.text(safeDecode(reason), margin, y);
                y += bodyLineHeight;
                doc.setFont('Helvetica', 'normal');
            }
            const logged = issue?.loggedAt;
            if (hasValue(logged)) {
                const when = formatDate(logged, true);
                doc.setFontSize(10);
                doc.text(when, margin, y);
                y += bodyLineHeight - 1;
                doc.setFontSize(11);
            }
            const body = getFirstValue(issue?.comment);
            if (hasValue(body)) {
                writeWrapped(body);
            }
        });
        y += sectionGap;
        doc.setFontSize(11);
    }

    const requestedActions = Array.isArray(data?.requestedActions) ? data.requestedActions : [];
    if (requestedActions.length > 0) {
        const sortedActions = [...requestedActions].sort((x, y) => {
            const xTime = getActionSentTimeMs(x) ?? 0;
            const yTime = getActionSentTimeMs(y) ?? 0;
            return xTime - yTime;
        });
        writeBoldLine(`Actions / discussion (${sortedActions.length})`);
        sortedActions.forEach((act, idx) => {
            ensureSpace(4);
            if (idx > 0) {
                doc.setLineWidth(0.2);
                doc.line(margin, y, pageWidth - margin, y);
                y += sectionGap;
            }
            const who = getFirstValue(act?.requestedBy, act?.requested_by);
            const label = getActionLabel(act) || getFirstValue(act?.action, act?.Action);
            const timeMs = getActionSentTimeMs(act);
            const timeStr = timeMs ? formatDate(new Date(timeMs).toISOString(), true) : '';
            const headerBits = [who, label, timeStr].filter((b) => hasValue(b));
            if (headerBits.length) {
                doc.setFont('Helvetica', 'bold');
                doc.setFontSize(10);
                const headerText = headerBits.join(' — ');
                doc.splitTextToSize(safeDecode(headerText), contentWidth).forEach((line) => {
                    ensureSpace(1);
                    doc.text(line, margin, y);
                    y += bodyLineHeight - 1;
                });
                doc.setFont('Helvetica', 'normal');
                doc.setFontSize(11);
            }
            const comment = getFirstValue(act?.actionComment, act?.action_comment);
            if (hasValue(comment)) {
                writeWrapped(comment);
            }
        });
        y += sectionGap;
    }

    const srxStatus = getFirstValue(data?.srxStatus);
    const pharmacyStatus = getFirstValue(data?.pharmacyStatus);
    if (hasValue(srxStatus) || hasValue(pharmacyStatus)) {
        ensureSpace(2);
        doc.setLineWidth(0.5);
        doc.line(margin, y, pageWidth - margin, y);
        y += sectionGap;
        if (hasValue(srxStatus)) {
            doc.text(`Resolution: ${safeDecode(srxStatus)}`, margin, y);
            y += bodyLineHeight;
        }
        if (hasValue(pharmacyStatus)) {
            doc.setFontSize(10);
            doc.text(`Pharmacy resolution: ${safeDecode(pharmacyStatus)}`, margin, y);
            y += bodyLineHeight;
            doc.setFontSize(11);
        }
    }

    return doc;
};

export const printAlfWidgetList = (data, title = 'ALF Alerts') => {
    // Landscape + more columns so the whole alert detail (drug, directions, qty, day supply,
    // qty remaining, etc.) prints in one list instead of requiring a per-alert print.
    const doc = new jsPDF({ orientation: 'landscape' });
    const valueOrDash = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        return String(value);
    };

    const tableColumn = [
        'Patient',
        'Rx #',
        'Facility',
        'Station',
        'Drug Name',
        'Directions',
        'Qty',
        'Days Supply',
        'Qty Remaining',
        'Rx Prescriber',
    ];
    const tableRows = data.map((row) => [
        valueOrDash(row.patName),
        valueOrDash(row.rxNo),
        valueOrDash(row.facName),
        valueOrDash(row.nsID),
        valueOrDash(row.drugLabelName),
        valueOrDash(row.sig),
        valueOrDash(row.qty),
        valueOrDash(row.daysSupply),
        valueOrDash(row.qtyRemaining),
        valueOrDash(row.prescribingDoctor),
    ]);

    const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
    doc.setFontSize(18);
    doc.text(title, pageWidth / 2, 15, { align: 'center' });
    doc.setLineWidth(0.5);
    doc.line(14, 20, pageWidth - 14, 20);
    doc.setFont('Helvetica');

    doc.autoTable(tableColumn, tableRows, {
        startY: 26,
        headStyles: { fillColor: '#452f69' },
        styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
        columnStyles: { 5: { cellWidth: 45 } },
    });
    return doc;
};

export const printAlfWidgetTicket = (data) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
    const valueOrDash = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        return String(value);
    };
    const getFirstValue = (...values) => values.find((v) => v !== null && v !== undefined && v !== '');
    const formatDateIfPresent = (value) => {
        if (!value) return '-';
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? valueOrDash(value) : parsed.toLocaleDateString();
    };
    const patientName = getFirstValue(data.patName, data.patientName);
    const facilityName = getFirstValue(data.facName, data.facilityName);
    const nursingStation = getFirstValue(data.nsID, data.nursingStationId);
    const prescribername = getFirstValue(data.phName);
    const prescribingDoctor = getFirstValue(data.prescribingDoctor);
    const patientDoctor = getFirstValue(data.patDoctor);
    const drugName = getFirstValue(data.drugLabelName, data.drugName);
    const alertedOn = formatDateIfPresent(getFirstValue(data.alertCreatedOn, data.dispenseDt));
    const expireDate = formatDateIfPresent(getFirstValue(data.expireDt, data.expiration));
    doc.setFontSize(22);
    doc.text('ALF - New Script Needed', pageWidth / 2, 20, { align: 'center' });
    doc.setLineWidth(0.5);
    doc.line(14, 25, pageWidth - 14, 25);
    doc.setFont('Helvetica');
    doc.setFontSize(15);
    doc.text('Patient Name: ' + valueOrDash(patientName), 14, 35);
    doc.text('Facility Name: ' + valueOrDash(facilityName), 14, 43);
    doc.text('Nursing Station: ' + valueOrDash(nursingStation), 14, 51);
    doc.text('Physician Name: ' + valueOrDash(patientDoctor), 14, 59);
    // doc.text('Prescriber Name: ' + valueOrDash(prescribername), 14, 67);
    doc.text('Drug Name: ' + valueOrDash(drugName), 14, 75);
    doc.text('Rx Prescriber: ' + valueOrDash(prescribingDoctor), 14, 83);
    // doc.text('Alerted On: ' + valueOrDash(alertedOn), 14, 91);
    doc.text('Rx Expiration: ' + valueOrDash(expireDate), 14, 99);

    const issueText = formatIssuesForListCell(data);
    if (issueText) {
        let y = 99;
        doc.setFontSize(12);
        doc.text('Comments:', 14, y);
        y += 9;
        doc.setFontSize(10);
        let decoded = issueText;
        try {
            decoded = decode(issueText);
        } catch {
            decoded = issueText;
        }
        const lines = doc.splitTextToSize(decoded, pageWidth - 28);
        lines.forEach((line) => {
            if (y > doc.internal.pageSize.getHeight() - 20) {
                doc.addPage();
                y = 20;
            }
            doc.text(line, 14, y);
            y += 7;
        });
    }
    return doc;
};

export default generatePDF;
